import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { AlertOctagon, Menu } from "lucide-react";
import { api } from "../lib/api";
import { Logo } from "../components/Layout";

const LINKS = [["", "Overview"], ["members", "Members"], ["content", "Content editor"], ["approvals", "Directory approvals"], ["registrations", "Event registrations"], ["forum", "Forum moderation"], ["contacts", "Contact submissions"], ["matches", "Match Requests"], ["outbox", "Email Outbox"], ["health", "System Health"], ["exports", "Data export"], ["audit", "Audit log"], ["settings", "Tier settings"]];

export function HealthBanner() {
  const [inc, setInc] = useState([]);
  const loc = useLocation();
  useEffect(() => {
    const load = () => api.get("/admin/health/banner").then((r) => setInc(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    window.addEventListener("kh-health-changed", load);
    return () => { clearInterval(t); window.removeEventListener("kh-health-changed", load); };
  }, [loc.pathname]);
  if (!inc.length) return null;
  return (
    <div role="alert" className="flex flex-wrap items-center gap-3 bg-[#B42318] px-4 py-3 text-sm text-white" data-testid="admin-alert-banner">
      <AlertOctagon className="h-5 w-5" aria-hidden="true" />
      <strong>{inc.length} open incident{inc.length > 1 ? "s" : ""}:</strong>
      <span>{inc.map((i) => i.title).join(" · ")}</span>
      <Link to="/admin/health" className="ml-auto rounded-full bg-white px-3 py-1 text-xs font-semibold text-[#B42318]" data-testid="admin-banner-view-link">View System Health</Link>
    </div>
  );
}

export default function AdminLayout() {
  const [open, setOpen] = useState(false);
  const cls = ({ isActive }) => `block rounded-lg px-3 py-2 text-sm font-medium ${isActive ? "bg-ochre text-ink" : "text-paper/85 hover:bg-white/10"}`;
  return (
    <div className="min-h-screen bg-paper">
      <HealthBanner />
      <div className="flex">
        <aside className={`${open ? "block" : "hidden"} fixed inset-y-0 left-0 z-40 w-64 overflow-y-auto bg-ink p-4 lg:static lg:block lg:min-h-screen`} aria-label="Admin navigation">
          <div className="mb-6 rounded-xl bg-paper p-2"><Logo /></div>
          <nav className="space-y-0.5">
            {LINKS.map(([to, l]) => <NavLink key={l} end={to === ""} to={`/admin${to ? `/${to}` : ""}`} onClick={() => setOpen(false)} className={cls} data-testid={`admin-nav-${to || "overview"}`}>{l}</NavLink>)}
          </nav>
          <div className="mt-6 border-t border-white/10 pt-4 text-sm"><Link to="/" className="text-paper/80 hover:text-ochre" data-testid="admin-back-to-site">← Back to site</Link><br /><Link to="/demo-access" className="text-paper/80 hover:text-ochre">Account access</Link></div>
        </aside>
        {open && <button className="fixed inset-0 z-30 bg-black/30 lg:hidden" aria-label="Close menu" onClick={() => setOpen(false)} />}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 border-b border-line bg-white px-4 py-3 lg:hidden">
            <button onClick={() => setOpen(true)} aria-label="Open admin menu" data-testid="admin-mobile-menu-btn"><Menu className="h-6 w-6" /></button><span className="font-serif text-lg">Admin</span>
          </div>
          <main id="main" className="p-4 sm:p-8"><Outlet /></main>
        </div>
      </div>
    </div>
  );
}

export const AdminHead = ({ title, intro, children }) => (
  <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
    <div><h1 className="text-3xl sm:text-4xl">{title}</h1>{intro && <p className="mt-1 text-sm text-[#4a4f59]">{intro}</p>}</div>
    {children}
  </div>
);
